import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { type WaveThemeMode } from '../styles/theme';

/**
 * Theme context value interface
 */
export interface ThemeContextValue {
  /** Current theme mode */
  mode: WaveThemeMode;
  
  /** Toggle between light and dark mode */
  toggleTheme: () => void;
  
  /** Set specific theme mode */
  setTheme: (mode: WaveThemeMode) => void;
  
  /** Whether theme is currently being loaded from storage */
  isLoading: boolean;
}

/**
 * Theme context for managing app-wide theme state
 */
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Props for ThemeProvider component
 */
export interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme mode (for testing or fallback) */
  defaultMode?: WaveThemeMode;
  /** Initial theme from external source (e.g., user preferences) */
  initialTheme?: WaveThemeMode;
  /** Callback when theme changes (for external persistence) */
  onThemeChange?: (theme: WaveThemeMode) => void;
}

/**
 * Theme provider component that manages theme state and persistence
 * 
 * Features:
 * - Immediate localStorage persistence (prevents flash on refresh)
 * - Syncs with Clerk user metadata for cross-device consistency
 * - System preference detection as fallback
 * - Debounced Clerk updates to prevent rate limiting
 */
export function ThemeProvider({ 
  children, 
  defaultMode = 'dark', 
  initialTheme,
  onThemeChange 
}: ThemeProviderProps) {
  // Initialize with immediate preference to avoid flash
  const [mode, setMode] = useState<WaveThemeMode>(() => {
    try {
      // First check localStorage for immediate theme (avoids flash)
      const stored = localStorage.getItem('wave-theme');
      console.log('🎨 ThemeContext: initializing, localStorage theme:', stored);
      if (stored === 'light' || stored === 'dark') {
        console.log('🎨 ThemeContext: using stored theme:', stored);
        return stored;
      }
      
      // Fallback to system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const systemTheme = prefersDark ? 'dark' : 'light';
      console.log('🎨 ThemeContext: using system preference:', systemTheme);
      return systemTheme;
    } catch (error) {
      console.log('🎨 ThemeContext: error during initialization, using default:', defaultMode, error);
      return defaultMode;
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Sync with Clerk user preferences when available
   */
  useEffect(() => {
    console.log('🎨 ThemeContext: sync effect running, initialTheme:', initialTheme, 'currentMode:', mode, 'isLoading:', isLoading);
    const syncTheme = () => {
      try {
        // If Clerk has a preference, use it and update localStorage
        if (initialTheme && (initialTheme === 'light' || initialTheme === 'dark')) {
          if (initialTheme !== mode) {
            console.log('🎨 ThemeContext: Clerk theme differs from current, updating mode from', mode, 'to', initialTheme);
            setMode(initialTheme);
            localStorage.setItem('wave-theme', initialTheme);
          } else {
            console.log('🎨 ThemeContext: Clerk theme matches current mode:', initialTheme);
          }
        } else {
          console.log('🎨 ThemeContext: no valid Clerk theme available');
        }
      } catch (error) {
        console.warn('Failed to sync theme with Clerk:', error);
      } finally {
        // Mark as loaded after first sync attempt
        if (isLoading) {
          console.log('🎨 ThemeContext: setting isLoading to false');
          setIsLoading(false);
        }
      }
    };

    syncTheme();
  }, [initialTheme, isLoading]); // Removed 'mode' to prevent feedback loop

  /**
   * Notify parent when theme changes (for external persistence like Clerk)
   */
  useEffect(() => {
    console.log('🎨 ThemeContext: onThemeChange effect, mode:', mode, 'isLoading:', isLoading);
    if (!isLoading) {
      console.log('🎨 ThemeContext: calling onThemeChange with mode:', mode);
      onThemeChange?.(mode);
    }
  }, [mode, isLoading, onThemeChange]);

  /**
   * Toggle between light and dark mode
   */
  const toggleTheme = () => {
    console.log('🎨 ThemeContext: toggleTheme called, current mode:', mode);
    setMode(current => {
      const newMode = current === 'light' ? 'dark' : 'light';
      console.log('🎨 ThemeContext: switching from', current, 'to', newMode);
      // Save immediately to localStorage to prevent flash on refresh
      try {
        localStorage.setItem('wave-theme', newMode);
        console.log('🎨 ThemeContext: saved to localStorage:', newMode);
      } catch (error) {
        console.warn('Failed to save theme to localStorage:', error);
      }
      return newMode;
    });
  };

  /**
   * Set specific theme mode
   */
  const setTheme = (newMode: WaveThemeMode) => {
    setMode(newMode);
    // Save immediately to localStorage to prevent flash on refresh
    try {
      localStorage.setItem('wave-theme', newMode);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  };

  const contextValue: ThemeContextValue = {
    mode,
    toggleTheme,
    setTheme,
    isLoading
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 * 
 * @throws Error if used outside of ThemeProvider
 */
export function useThemeContext(): ThemeContextValue {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  
  return context;
}

/**
 * Hook to get just the current theme mode
 * Convenience hook for components that only need to read the theme
 */
export function useThemeMode(): WaveThemeMode {
  const { mode } = useThemeContext();
  return mode;
}

export default ThemeProvider;